import { useAuth0 } from '@auth0/auth0-react'
import { byPrefixAndName } from '@awesome.me/kit-f16c5d7bca/icons'

import {
  Box,
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Tooltip
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toastr } from 'react-redux-toastr'
import { useHistory } from 'react-router-dom'

import branditRails from '../../api/brandit_rails'
import { currentAdminPropTypes } from '../../reducers/adminsReducer'
import { handleLogout } from '../../redux/actions/auth'

import MFAvatar from '../MFAvatar'
import { useMFNav } from './MFNav.Provider'

/**
 * @typedef {Object} MFNavAvatarMenuProps
 * @property {'avatar' | 'icon' | 'responsive'} [buttonType='avatar'] - The type of button to render.
 * @property {boolean} [condition] - The condition to respond to if buttonType is 'responsive'.
 */

const MFNavAvatarMenuIconButton = () => (
  <MenuButton
    as={IconButton}
    variant="ghost"
    colorScheme="neutral"
    icon={<FontAwesomeIcon icon={byPrefixAndName['fas']['ellipsis-vertical']} />}
    aria-label="Open Menu"
    size="sm"
    justifyContent="center"
  />
)

/**
 *
 * @param {Object} props
 * @param {import('../../reducers/adminsReducer').CurrentAdmin} props.currentAdmin
 * @param {import('./MFNav.Provider').MFNavContextType['adminType']} props.adminType
 * @returns
 */
const MFNavAvatarMenuAvatarButton = ({ currentAdmin, adminType }) => (
  <MenuButton as={Button} variant="link" minW="0" aria-label="Open Menu">
    <MFAvatar userImg={currentAdmin.avatar} adminType={adminType} name={currentAdmin.name} size="sm" />
  </MenuButton>
)

MFNavAvatarMenuAvatarButton.propTypes = {
  currentAdmin: currentAdminPropTypes,
  adminType: PropTypes.oneOf(['Super Admin', 'Client Admin', 'Admin'])
}

/**
 * MFNavAvatarMenu component to render the navigation avatar menu.
 *
 * @param {Object} props - The properties for the component.
 * @param {import('./MFNav.Provider').MFNavContextType['current']['admin']} props.currentAdmin - The current admin object.
 * @param {import('./MFNav.Provider').MFNavContextType['current']['type']} props.adminType - The type of admin user.
 * @param {'avatar' | 'icon'} [props.buttonType='avatar'] - The type of button to render. @default 'avatar'
 * @returns {JSX.Element} The navigation avatar menu component.
 */
function MFNavAvatarMenuComponent({ currentAdmin, adminType, buttonType = 'avatar' }) {
  const { skin, modal, menu } = useMFNav()
  const dispatch = useDispatch()
  const { logout, isAuthenticated } = useAuth0()
  const history = useHistory()

  const [selectedPortalId, setSelectedPortalId] = useState(null)

  const handleLogoutButton = useCallback(async () => {
    if (isAuthenticated)
      await logout({
        logoutParams: { returnTo: `${window.location.origin}/login` }
      })
    await dispatch(handleLogout(currentAdmin))
  }, [currentAdmin, dispatch, isAuthenticated, logout])

  const skinIcon = useMemo(() => (skin.value === 'dark' ? 'sun-bright' : 'moon'), [skin.value])

  const UserMenuButton = useMemo(() => {
    if (buttonType === 'avatar') {
      const AvatarButton = () => <MFNavAvatarMenuAvatarButton adminType={adminType} currentAdmin={currentAdmin} />

      return AvatarButton
    }

    return MFNavAvatarMenuIconButton
  }, [adminType, buttonType, currentAdmin])

  const associatedStoreOptions = useMemo(
    () =>
      currentAdmin?.associated_portal_info?.map(portal => ({
        label: portal.portal_name,
        value: portal.portal_id
      })),
    [currentAdmin?.associated_portal_info]
  )

  const ssoStoreLogin = useCallback(
    async (e, selectedPortalId = null) => {
      e.preventDefault()

      try {
        const userId = currentAdmin?.user?.id
        let portalId = null

        if (!selectedPortalId && currentAdmin?.associated_portal_info?.length > 1) {
          modal.toggle(prev => !prev)
          return null
        }

        if (selectedPortalId) {
          portalId = selectedPortalId
          modal.toggle(false)
        } else if (currentAdmin.associated_portal_info.length === 1)
          portalId = currentAdmin.associated_portal_info[0].portal_id

        if (!portalId) throw new Error('No portal ID found. Please contact administrator.')

        const { data } = await branditRails.get(`/get_sso_url_customer_show/user_id/${userId}/portal_id/${portalId}`)

        if (data.url) window.location.href = data.url
      } catch (err) {
        toastr.error(`Could not log in as customer. Error: ${err.message}`)
      }
    },
    [currentAdmin.associated_portal_info, currentAdmin?.user?.id, modal]
  )

  return (
    <>
      <Menu
        size="xs"
        placement={buttonType === 'avatar' ? 'bottom-start' : 'right'}
        isOpen={menu.state.trueDockState ? false : undefined}
      >
        <UserMenuButton />
        <MenuList>
          {adminType === 'Client Admin' && currentAdmin.user && (
            <MenuItem
              justifyContent="start"
              icon={<FontAwesomeIcon icon={byPrefixAndName['far']['shield-keyhole']} />}
              aria-label="Access store"
              onClick={e => ssoStoreLogin(e, selectedPortalId)}
              isDisabled={!currentAdmin?.user?.sso_id}
            >
              Access Store
            </MenuItem>
          )}
          <MenuItem
            justifyContent="start"
            icon={<FontAwesomeIcon icon={byPrefixAndName['far'][skinIcon]} />}
            aria-label="Toggle Dark Mode"
            onClick={skin.toggle}
          >
            {skin.value === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </MenuItem>
          <MenuItem
            isDisabled
            justifyContent="start"
            icon={<FontAwesomeIcon icon={byPrefixAndName['far']['user']} />}
            aria-label="Profile (Future)"
          >
            Profile (coming soon)
          </MenuItem>
          <MenuItem
            isDisabled
            justifyContent="start"
            icon={<FontAwesomeIcon icon={byPrefixAndName['far']['gear']} />}
            aria-label="Preferences (Future)"
          >
            Preferences (coming soon)
          </MenuItem>
          <MenuItem
            justifyContent="start"
            icon={<FontAwesomeIcon icon={byPrefixAndName['far']['key']} />}
            aria-label="Update Password"
            onClick={() =>
              history.push(
                currentAdmin.logged_in_as === 'Organization'
                  ? '/update_password/organization_admin'
                  : '/update_password'
              )
            }
          >
            Update Password
          </MenuItem>
          <MenuItem
            justifyContent="start"
            icon={<FontAwesomeIcon icon={byPrefixAndName['far']['right-from-bracket']} />}
            onClick={handleLogoutButton}
            aria-label="Sign Out"
          >
            Sign Out
          </MenuItem>
        </MenuList>
      </Menu>
      <Modal isOpen={modal.value} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Store</ModalHeader>
          <ModalBody>
            <Text>There are multiple stores associated with this account. Pleaseselect one to continue.</Text>
            <Select variant="outline" onChange={e => setSelectedPortalId(e.target.value)}>
              <option value={null}>Select Store</option>
              {(associatedStoreOptions ?? []).map(opt => (
                <option key={opt.label} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter as={HStack} spacing={2}>
            <Button colorScheme="neutral" onClick={() => modal.toggle(false)}>
              Cancel
            </Button>
            <Button onClick={e => ssoStoreLogin(e, selectedPortalId)}>Access Store</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

MFNavAvatarMenuComponent.propTypes = {
  currentAdmin: currentAdminPropTypes,
  adminType: PropTypes.oneOf(['Super Admin', 'Client Admin', 'Admin']),
  buttonType: PropTypes.oneOf(['avatar', 'icon']) /** @default 'avatar' */
}

/**
 *
 * @param {MFNavAvatarMenuProps} props
 * @returns  {JSX.Element} The navigation avatar menu component.
 */
export default function MFNavAvatarMenu({ buttonType = 'responsive', condition = false, ...props }) {
  const {
    current: { admin, agency, org, type, loggedInAs }
  } = useMFNav()

  const association = useMemo(() => {
    if (loggedInAs === 'Agency') return agency?.name ?? null
    return org?.name ?? null
  }, [agency?.name, org?.name, loggedInAs])

  if (buttonType === 'avatar')
    return <MFNavAvatarMenuComponent buttonType="avatar" currentAdmin={admin} adminType={type} />
  if (buttonType === 'icon') return <MFNavAvatarMenuComponent buttonType="icon" currentAdmin={admin} adminType={type} />

  return (
    <HStack w="full" spacing="2" justify={condition ? 'center' : 'space-between'} {...props}>
      <HStack spacing="3" alignItems="center" hidden={condition}>
        <MFAvatar size="sm" userImg={admin.avatar} adminType={type} name={admin.name} withBadge />
        {/* <Tooltip label={`${admin.name}${association ? ` - ${association}` : ''}`} placement="top"> */}
        <Tooltip
          label={
            <Box>
              <Text mb="0" fontSize="sm" fontWeight="semibold">
                {admin.name}
              </Text>
              {association && (
                <Text mb="0" fontSize="xs" fontWeight="medium">
                  {association}
                </Text>
              )}
            </Box>
          }
          placement="right-start"
        >
          <Box>
            <Text mb="0" fontSize="sm" fontWeight="semibold" noOfLines={1}>
              {admin.name}
            </Text>
            {association && (
              <Text mb="0" fontSize="xs" fontWeight="medium" noOfLines={1}>
                {association}
              </Text>
            )}
          </Box>
        </Tooltip>
      </HStack>
      <MFNavAvatarMenuComponent buttonType={condition ? 'avatar' : 'icon'} currentAdmin={admin} adminType={type} />
    </HStack>
  )
}

MFNavAvatarMenu.propTypes = {
  buttonType: PropTypes.oneOf(['avatar', 'icon', 'responsive']) /** @default 'responsive' */,
  condition: PropTypes.bool /** @default {false} */
}
